import { isNumber, pick } from 'lodash';
import { FilterWithOperatorProps } from '../types';
import {
  isNumberInterval,
  NUMBER_FORMAT_PERCENTAGE,
  Operator,
} from '@ardoq/api-types';
import { NumberInput, NumberInputProps } from '@ardoq/forms';
import { DEFAULT_OPERATOR_TO_LABEL } from '@ardoq/query-builder';
import { Select, SelectOption } from '@ardoq/select';
import { isBetweenOptionSelected, isEmptyOptionSelected } from '../utils';
import { removeExcludedOperators } from './filterUtils';
import { RemoveFilterButtonInTopRightCorner, SelectContainer } from './atoms';
import { Maybe } from '@ardoq/common-helpers';
import { percentageEditValueToStoredValue } from '@ardoq/locale';
import type { NumericColumnFilterInputLabels } from '../labels';

export type { NumericColumnFilterInputLabels };

const NUMBER_OPERATOR_SELECT_OPTIONS = Object.entries(
  pick(DEFAULT_OPERATOR_TO_LABEL, [
    Operator.EQUAL,
    Operator.NOT_EQUAL,
    Operator.GREATER,
    Operator.LESS,
    Operator.IS_EMPTY,
    Operator.IS_NOT_EMPTY,
    Operator.BETWEEN_INCLUSIVE,
  ])
).map(([operator, label]) => ({
  label,
  value: operator,
})) as SelectOption<Operator>[];

const DEFAULT_LABELS = {
  searchPlaceholder: 'Search...',
  startHigherError:
    "The 'From' value must be less than the 'To' value. Enter a valid range.",
  fromPrefix: 'From',
  toPrefix: 'To',
} satisfies Required<NumericColumnFilterInputLabels>;

const DEFAULT_REMOVE_FILTER_BUTTON_LABEL = 'Remove filter';

const FilterNumberInput = ({
  placeholder,
  ...props
}: Omit<NumberInputProps, 'allowDecimals'>) => (
  <NumberInput allowDecimals placeholder={placeholder} {...props} />
);

const NumberInputCombination = ({
  column,
  expression: { value, displayValue, operator, formattedDisplayValue },
  isReadOnly,
  changeFilterExpression,
  labels,
}: FilterWithOperatorProps & {
  labels: Required<NumericColumnFilterInputLabels>;
}) => {
  if (isEmptyOptionSelected(operator)) return null;

  const isPercentage = column.numberFormatOptions === NUMBER_FORMAT_PERCENTAGE;

  if (isBetweenOptionSelected(operator)) {
    const { start, end } = isNumberInterval(value)
      ? value
      : { start: null, end: null };

    // Use displayValue if available, otherwise use the original value
    const displayInterval = isNumberInterval(displayValue)
      ? displayValue
      : { start, end };

    // formattedDisplayValue is always an object for intervals
    const formattedInterval =
      typeof formattedDisplayValue === 'object' ? formattedDisplayValue : {};

    const isStartHigher =
      isNumber(displayInterval.start) &&
      isNumber(displayInterval.end) &&
      displayInterval.start > displayInterval.end;

    return (
      <>
        <FilterNumberInput
          value={displayInterval.start ?? undefined}
          displayValue={formattedInterval.start}
          onValueChange={newValue => {
            // Convert percentage back to fraction for storage
            const storedValue =
              isPercentage && newValue !== undefined
                ? percentageEditValueToStoredValue(newValue)
                : !isFinite(newValue)
                  ? null
                  : newValue;
            changeFilterExpression(column, {
              value: {
                start: storedValue,
                end,
              },
              operator,
            });
          }}
          errorMessage={isStartHigher ? '' : undefined}
          placeholder={labels.searchPlaceholder}
          prefix={labels.fromPrefix}
          hasError={isStartHigher}
          max={displayInterval.end ?? undefined}
          isReadOnly={isReadOnly}
        />

        <FilterNumberInput
          value={displayInterval.end ?? undefined}
          displayValue={formattedInterval.end}
          onValueChange={newValue => {
            // Convert percentage back to fraction for storage
            const storedValue =
              isPercentage && newValue !== undefined
                ? percentageEditValueToStoredValue(newValue)
                : !isFinite(newValue)
                  ? null
                  : newValue;

            changeFilterExpression(column, {
              value: {
                start,
                end: storedValue,
              },
              operator,
            });
          }}
          placeholder={labels.searchPlaceholder}
          prefix={labels.toPrefix}
          errorMessage={isStartHigher ? labels.startHigherError : undefined}
          min={displayInterval.start ?? undefined}
          isReadOnly={isReadOnly}
        />
      </>
    );
  }

  // Use displayValue if available, otherwise use the original value
  const editValue =
    displayValue !== undefined && isNumber(displayValue)
      ? displayValue
      : isNumber(value)
        ? value
        : undefined;

  return (
    <FilterNumberInput
      value={editValue}
      displayValue={
        typeof formattedDisplayValue === 'string'
          ? formattedDisplayValue
          : undefined
      }
      onValueChange={newValue => {
        // Convert percentage back to fraction for storage
        const storedValue =
          isPercentage && newValue !== undefined
            ? percentageEditValueToStoredValue(newValue)
            : !isFinite(newValue)
              ? null
              : newValue;
        changeFilterExpression(column, {
          value: storedValue,
          operator,
        });
      }}
      placeholder={labels.searchPlaceholder}
      isReadOnly={isReadOnly}
    />
  );
};

type NumericColumnFilterInputProps = FilterWithOperatorProps & {
  menuTarget: Maybe<HTMLElement>;
};

export const NumericColumnFilterInput = ({
  column,
  expression,
  removeFilter,
  changeFilterExpression,
  menuTarget,
  isReadOnly,
  excludedOperators,
  labels,
  removeFilterButtonLabel = DEFAULT_REMOVE_FILTER_BUTTON_LABEL,
}: NumericColumnFilterInputProps & {
  labels?: NumericColumnFilterInputLabels;
  removeFilterButtonLabel?: string;
}) => {
  const mergedLabels = { ...DEFAULT_LABELS, ...labels };
  const selectedOperator = expression.operator || Operator.EQUAL;
  return (
    <SelectContainer>
      {!isReadOnly && (
        <RemoveFilterButtonInTopRightCorner
          onClick={() => removeFilter(column.key)}
          label={removeFilterButtonLabel}
        />
      )}
      <Select
        isReadOnly={isReadOnly}
        label={column.label}
        options={removeExcludedOperators(
          NUMBER_OPERATOR_SELECT_OPTIONS,
          excludedOperators
        )}
        value={selectedOperator}
        onChange={operatorOption =>
          changeFilterExpression(column, {
            value: isEmptyOptionSelected(operatorOption?.value)
              ? null
              : expression.value,
            operator: operatorOption?.value ?? Operator.EQUAL,
          })
        }
        menuPortalTarget={menuTarget}
      />
      <NumberInputCombination
        column={column}
        expression={{ ...expression, operator: selectedOperator }}
        changeFilterExpression={changeFilterExpression}
        removeFilter={removeFilter}
        isReadOnly={isReadOnly}
        labels={mergedLabels}
      />
    </SelectContainer>
  );
};
